import React from 'react'
import styled from 'styled-components'

export type TagVariant = 'tinted' | 'solid'
export type TagShape = 'pill' | 'rounded'

export interface TagProps {
  children: React.ReactNode
  variant?: TagVariant
  color?: string
  bg?: string
  fg?: string
  border?: string
  bodyAlpha?: number
  borderAlpha?: number
  shape?: TagShape
  multiline?: boolean
  className?: string
}

const hexToRgba = (hex: string, alpha: number): string => {
  const h = hex.replace('#', '')
  const full =
    h.length === 3
      ? h
          .split('')
          .map((c) => c + c)
          .join('')
      : h
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export const Tag: React.FC<TagProps> = ({
  children,
  variant = 'tinted',
  color,
  bg,
  fg,
  border,
  bodyAlpha = 0.14,
  borderAlpha = 0.45,
  shape = 'pill',
  multiline = false,
  className,
}) => {
  let resolvedBg = bg
  let resolvedBorder = border
  let resolvedFg = fg

  if (variant === 'tinted' && color) {
    resolvedBg ??= hexToRgba(color, bodyAlpha)
    resolvedBorder ??= hexToRgba(color, borderAlpha)
    resolvedFg ??= color
  }
  if (variant === 'solid') {
    resolvedBg ??= color
    resolvedBorder ??= 'transparent'
  }

  const content =
    multiline && typeof children === 'string'
      ? children.replace(/\s+/g, '\n')
      : children

  return (
    <Root
      className={className}
      $bg={resolvedBg}
      $border={resolvedBorder}
      $fg={resolvedFg}
      $shape={shape}
      $multiline={multiline}
    >
      {content}
    </Root>
  )
}

export default Tag

const Root = styled.span<{
  $bg?: string
  $border?: string
  $fg?: string
  $shape: TagShape
  $multiline: boolean
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  padding: 0.125rem 0.5rem;
  border-radius: ${({ $shape, theme }) =>
    $shape === 'pill' ? theme.radii.pill : '0.75rem'};
  background: ${({ $bg, theme }) => $bg ?? theme.colors.accentSoft};
  border: 1px solid ${({ $border, theme }) => $border ?? theme.colors.line};
  color: ${({ $fg, theme }) => $fg ?? theme.colors.ink2};
  font-size: ${({ theme }) => theme.text.xs};
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: 0.02em;
  text-align: center;
  white-space: ${({ $multiline }) => ($multiline ? 'pre-line' : 'nowrap')};
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: ${({ theme }) => theme.textMobile.xs};
  }
  ${({ $multiline }) =>
    !$multiline &&
    `
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`
